import React, { useState, useEffect } from 'react';
import {
    Popover,
    Typography,
    Box,
    Grid,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    IconButton,
    Paper
} from '@mui/material';
import {
    Close as CloseIcon,
    AttachMoney as AttachMoneyIcon,
    TrendingUp as TrendingUpIcon,
    Security as SecurityIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { getSymbolInfo } from '../../api/symbolApi';

const SymbolInfoModal = ({ open, onClose, symbolName, anchorEl }) => {
    const [symbolInfo, setSymbolInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);


    useEffect(() => {
        if (open && symbolName) {
            fetchSymbolInfo();
        }
    }, [open, symbolName]);

    const fetchSymbolInfo = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getSymbolInfo(symbolName);
            setSymbolInfo(response);
        } catch (err) {
            setError(err.message || 'Failed to fetch symbol information');
        } finally {
            setLoading(false);
        }
    };

    const getSecurityTypeColor = (securityType) => {
        switch (securityType?.toLowerCase()) {
            case 'fx':
            case 'forex':
                return '#22c55e';
            case 'crypto':
                return '#f59e0b';
            case 'metal':
            case 'metals':
                return '#8b5cf6';
            case 'index':
            case 'indexes':
                return '#3b82f6';
            case 'stock':
            case 'stocks':
                return '#ef4444';
            default:
                return '#6b7280';
        }
    };

    const getSecurityTypeLabel = (securityType) => {
        switch (securityType?.toLowerCase()) {
            case 'fx':
                return 'Forex';
            case 'crypto':
                return 'Cryptocurrency';
            case 'metal':
            case 'metals':
                return 'Metals';
            case 'index':
            case 'indexes':
                return 'Indexes';
            case 'stock':
            case 'stocks':
                return 'Stocks';
            default:
                return securityType || 'Unknown';
        }
    };

    const formatCurrency = (value, currency) => {
        if (!value) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatNumber = (value, decimals = 0) => {
        if (!value) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(value);
    };

    const handleClose = () => {
        setSymbolInfo(null);
        setError(null);
        onClose();
    };

    
    if (!open) {
        return null;
    }
    
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                sx: {
                    backgroundColor: '#1e293b',
                    color: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    maxHeight: '80vh',
                    maxWidth: '600px',
                    minWidth: '500px',
                    overflow: 'auto',
                    position: 'relative',
                    zIndex: 9999
                }
            }}
            disableScrollLock={true}
            disablePortal={false}
            slotProps={{
                paper: {
                    sx: {
                        backgroundColor: '#1e293b',
                        color: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        maxHeight: '80vh',
                        maxWidth: '600px',
                        minWidth: '500px',
                        overflow: 'auto',
                        position: 'relative',
                        zIndex: 9999
                    }
                }
            }}
        >
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                p: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AttachMoneyIcon sx={{ color: '#06b6d4', fontSize: 28 }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                            {symbolName}
                        </Typography>
                        {symbolInfo && (
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {symbolInfo.description || `${symbolInfo.baseCurrency}/${symbolInfo.quoteCurrency}`}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{ 
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': { color: 'white' }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#06b6d4' }} />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                        {error}
                    </Alert>
                )}

                {symbolInfo && (
                    <Box>
                        {/* Security Type Badge */}
                        <Box sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                                icon={<SecurityIcon />}
                                label={getSecurityTypeLabel(symbolInfo.securityType)}
                                sx={{
                                    backgroundColor: getSecurityTypeColor(symbolInfo.securityType),
                                    color: 'white',
                                    fontWeight: 600,
                                    '& .MuiChip-icon': { color: 'white' }
                                }}
                            />
                            <Chip
                                label={symbolInfo.isActive ? 'Active' : 'Inactive'}
                                sx={{
                                    backgroundColor: symbolInfo.isActive ? '#22c55e' : '#ef4444',
                                    color: 'white',
                                    fontWeight: 600
                                }}
                            />
                        </Box>

                        <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                        {/* Symbol Information Grid */}
                        <Grid container spacing={3}>
                            {/* Basic Information */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" sx={{ 
                                    mb: 2, 
                                    color: '#06b6d4',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <InfoIcon />
                                    Basic Information
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            Symbol Name:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {symbolInfo.name}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            FIX Symbol:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {symbolInfo.fixSymbol}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            LP Symbol:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {symbolInfo.lpSymbolName}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            Digits:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {symbolInfo.digits}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            Tick Size:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatNumber(symbolInfo.tickSize, 5)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Trading Information */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" sx={{ 
                                    mb: 2, 
                                    color: '#06b6d4',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <TrendingUpIcon />
                                    Trading Information
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            Contract Size:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatNumber(symbolInfo.contractSize)}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            Min Lot Size:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatNumber(symbolInfo.minLotSize)}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            Max Lot Size:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatNumber(symbolInfo.maxLotSize)}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            Lot Step:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {formatNumber(symbolInfo.lotStep)}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            Base Currency:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {symbolInfo.baseCurrency}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                            Quote Currency:
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {symbolInfo.quoteCurrency}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {/* Additional Information */}
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                                
                                <Typography variant="h6" sx={{ 
                                    mb: 2, 
                                    color: '#06b6d4',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <InfoIcon />
                                    Additional Information
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                                LP ID:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {symbolInfo.lpId}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                                Created By:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {symbolInfo.createdBy}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                                Created At:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {symbolInfo.createdAt ? new Date(symbolInfo.createdAt).toLocaleDateString() : 'N/A'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                                Subscribed:
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {symbolInfo.isSubscribed ? 'Yes' : 'No'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Box>
        </Popover>
    );
};

export default SymbolInfoModal;
